import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Gift, ArrowRight } from 'lucide-react';
import { threshold, images } from '../data/village-data';
import { toast } from 'sonner';

export default function ThresholdPage() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState(0);
  const [showGate, setShowGate] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const t1 = setTimeout(() => setPhase(1), 800);
    const t2 = setTimeout(() => setPhase(2), 3200);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  const handleEnter = () => {
    const alreadyEntered = localStorage.getItem('village_entered');
    if (alreadyEntered) {
      navigate('/welcome');
    } else {
      setShowGate(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) { toast.error('Please enter your email'); return; }
    setSubmitting(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${API}/api/email-capture`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, name, source: 'village_gate' }),
      });
      localStorage.setItem('village_entered', 'true');
      localStorage.setItem('village_email', email);
      if (name) localStorage.setItem('village_name', name);
      navigate('/welcome');
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      data-testid="threshold-page"
      className="threshold-page"
      style={{
        backgroundImage: `linear-gradient(rgba(15,10,25,0.35), rgba(15,10,25,0.5)), url(${images.treeOfLife})`,
      }}
    >
      {/* Fireflies */}
      <div className="threshold-vfx">
        <div className="tvfx-firefly tvfx-tff-1" />
        <div className="tvfx-firefly tvfx-tff-2" />
        <div className="tvfx-firefly tvfx-tff-3" />
        <div className="tvfx-firefly tvfx-tff-4" />
        <div className="tvfx-firefly tvfx-tff-5" />
        <div className="tvfx-firefly tvfx-tff-6" />
      </div>

      <div className="threshold-content">
        {!showGate ? (
          <>
            <div className={`threshold-prelude ${phase >= 1 ? 'visible' : ''}`}>
              {threshold.prelude.map((line, i) => (
                <p key={i} className="prelude-line" style={{ animationDelay: `${i * 0.6}s` }}>
                  {line}
                </p>
              ))}
            </div>

            <div className={`threshold-main ${phase >= 2 ? 'visible' : ''}`}>
              <h1 data-testid="threshold-title" className="threshold-title">
                {threshold.title}
              </h1>
              <p className="threshold-subtext">{threshold.subtext}</p>

              <div data-testid="welcome-video" className="welcome-video-container" style={{ margin: '1.75rem auto', maxWidth: '620px' }}>
                <video
                  controls
                  playsInline
                  preload="metadata"
                  poster=""
                  className="welcome-video"
                >
                  <source src="/welcome-you.mp4" type="video/mp4" />
                </video>
              </div>

              <button
                data-testid="enter-village-btn"
                className="village-btn village-btn--primary"
                onClick={handleEnter}
              >
                {threshold.cta}
              </button>

              <p className="threshold-footnote">{threshold.footNote}</p>

              {/* Free chapter — secondary path for visitors who aren't ready to commit yet */}
              <div className="threshold-divider" aria-hidden="true">
                <span>or</span>
              </div>
              <Link
                to="/gift"
                className="threshold-gift-cta"
                data-testid="threshold-gift-cta"
              >
                <Gift size={16} strokeWidth={2.1} />
                <span className="threshold-gift-text">
                  <strong>Get this month&apos;s free chapter</strong>
                  <small>Chapter 1 of F.U.C.K. T.H.A.T.T. — yours instantly</small>
                </span>
                <ArrowRight size={16} strokeWidth={2.2} />
              </Link>
            </div>
          </>
        ) : (
          <div className="threshold-gate" data-testid="village-email-gate">
            <h2 className="threshold-gate-title">Before you enter The Village...</h2>
            <p className="threshold-gate-sub">Tell us where to find you. Your journey is about to begin.</p>
            <form className="threshold-gate-form" onSubmit={handleSubmit}>
              <input
                type="text"
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="threshold-gate-input"
                data-testid="gate-name-input"
              />
              <input
                type="email"
                placeholder="Your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="threshold-gate-input"
                data-testid="gate-email-input"
                required
              />
              <button
                type="submit"
                className="village-btn village-btn--primary threshold-gate-btn"
                data-testid="gate-submit-btn"
                disabled={submitting}
              >
                {submitting ? 'Opening the gates...' : 'Enter The Alchemy Village'}
              </button>
            </form>
            <p className="threshold-gate-note">Your information is sacred. We honor your privacy.</p>
          </div>
        )}
      </div>
    </section>
  );
}
